import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AppConfigService } from '@app/app-config.service';
import { SnackbarService } from '@app/core/services/snackbar/snackbar.service';
import { ImportStepsService, StepsComponent } from '@app/features/importsteps/services/import-steps.service';
import { FileTypeZip } from '@app/shared/models/file.interface';
import { ButtonColor } from '@components/buttons/button/button.component';
import { FileUploadModel } from '@components/form-controls/upload/upload/upload.component';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { take } from 'rxjs/internal/operators/take';
import { tap } from 'rxjs/internal/operators/tap';
import { DialogBaseComponent } from '../dialog-base/dialog-base.component';

/** The import dialog component */
export interface ImportConfirmDialogButton {
  label: string;
  color?: ButtonColor;
}

export interface ImportConfirmHeader {
  label: string;
  icon?: string;
}

export interface ImportDataDialogData {
  type: string;
  header: ImportConfirmHeader;
  templateType?: string;
  helpLink?: string;
  yesButton?: ImportConfirmDialogButton;
  noButton?: ImportConfirmDialogButton;
}

export interface ImportDataDialogResponse {
  type: string;
  file: FileUploadModel;
  cancel: boolean;
}

@Component({
  selector: 'vfs-import-data-dialog',
  templateUrl: './import-data-dialog.component.html',
  styleUrls: ['./import-data-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ImportDataDialogComponent extends DialogBaseComponent implements OnInit {
  /** The single file to import */
  file: FileUploadModel = null;

  waitDialog = null;

  /** Flag that indicates that a template is begin downloaded */
  downloadingTemplate = false;

  /** The template type (zip, xlsx) */
  templateType = null;

  stepsComponent: StepsComponent = null;
  stepsComponent$: Observable<any>;

  /** Constructor */
  constructor(
    protected http: HttpClient,
    public dialogRef: MatDialogRef<ImportDataDialogComponent>,
    public translateService: TranslateService,
    protected appConfigService: AppConfigService,
    protected snackBarService: SnackbarService,
    @Inject(MAT_DIALOG_DATA) public data: ImportDataDialogData,
    protected importStepsService: ImportStepsService
  ) {
    super(dialogRef);
    this.templateType = this.data.templateType;

    // This dialog only can be closed with the buttons... not clicking in the backdrop area
    dialogRef.disableClose = true;
  }

  ngOnInit(): void {
    this.loadStepsComponent();
  }

  loadStepsComponent() {
    this.stepsComponent$ = this.importStepsService
      .resolveComponent(this.data.type)
      .pipe(tap(res => (this.stepsComponent = res)));
  }

  /**
   * Cancel event
   */
  cancel() {
    let res: ImportDataDialogResponse = { type: this.data.type, file: null, cancel: true };
    this.dialogRef.close(res);
  }

  /**
   * Accept event
   */
  accept() {
    let res: ImportDataDialogResponse = {
      type: this.data.type,
      file: this.file,
      cancel: false
    };
    this.dialogRef.close(res);
  }

  /**
   * Method used to set the file to process
   *
   * @param file the file
   */
  importFileClick(file: FileUploadModel) {
    this.file = file;
  }

  /**
   * Translate a value that must be translated too
   *
   * @param value value
   * @return translation
   */
  translateValue(value: string): string {
    return this.translateService.instant('core.import.useTemplate', { value: this.translateService.instant(value) });
  }

  /**
   * Check if there's a file ready to import
   *
   * @return true if so
   */
  isFileUploaded(): boolean {
    return this.file != null;
  }

  /**
   * Show the "loading" effect
   *
   * @param onoff true to activate, false to deactivate
   */
  showLoading(onoff) {
    this.downloadingTemplate = onoff;
  }

  /**
   * Download a template
   *
   * @param type template type
   */
  downloadTemplate(type: string) {
    this.showLoading(true);
    let template: Blob = null;
    this.download(type)
      .pipe(take(1))
      .subscribe(
        data => {
          this.showLoading(false);
          template = new Blob([data], {
            type: this.downloadMimeType()
          });
        },
        error => {
          this.showLoading(false);
        },
        () => {
          this.getFile(template);
        }
      );
  }

  /**
   * Get the download mime type
   *
   * @return the type
   */
  downloadMimeType(): string {
    var mime = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    if (this.templateType === FileTypeZip.ZIP) {
      mime = 'application/zip';
    }
    return mime;
  }

  /**
   * Get the template or show an error if the file is empty
   *
   * @param thefile the template file
   * @param fileType the file type
   */
  protected getFile(thefile: Blob) {
    if (thefile.size == 0) {
      this.snackBarService.openSnackBar(this.translateService.instant('core.import.errorTemplateNotFound'), 'warning');
    } else {
      this.snackBarService.openSnackBar(
        this.translateService.instant('core.import.template.downloaded.snackbar'),
        'snackbar'
      );

      const anchor = document.createElement('a');
      anchor.download = new Date().getTime() + '.' + this.templateType;
      anchor.href = window.URL.createObjectURL(thefile);
      anchor.click();
      anchor.remove();
    }
  }

  /**
   * Download template
   *
   * @param selection selected files (file ids)
   * @return an Observable
   */
  download(type: string): Observable<any> {
    if (this.templateType === FileTypeZip.ZIP) {
      return this.http.post(`${this.appConfigService.getApi()}/importer/downloadTemplateAsZip/`, type, {
        responseType: 'blob'
      });
    } else {
      return this.http.post(`${this.appConfigService.getApi()}/importer/downloadTemplate/`, type, {
        responseType: 'blob'
      });
    }
  }

  /**
   * Check if there's defined a template
   *
   * @return true if so
   */
  hasTemplate(): boolean {
    return this.templateType !== null && this.templateType !== undefined;
  }
}
